/* eslint-disable no-unused-vars */
import DeleteIcon from '@mui/icons-material/Delete';
import {
  Box,
  Card,
  IconButton,
  Link,
  Stack,
  Tooltip,
  Typography,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Modal,
  CircularProgress,
  TextField,
  Snackbar,
  Alert,
  Fade,
  Divider,
  Chip,
} from '@mui/material';
import RestoreFromTrashIcon from '@mui/icons-material/RestoreFromTrash';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';

// 🛠️ REFINED: Milestone Status Helper
const MilestoneStatus = ({ label, status }) => {
  const statusValue = String(status || '').trim().toLowerCase();
  
  // 1. Strings that represent "Done" but are NOT dates
  const TICK_ONLY_STRINGS = ['done', '1', 'yes', 'true', 'received', 'found', 'issued'];
  
  // 2. Check if it's a valid date string
  const isDate = statusValue !== '' && !isNaN(Date.parse(statusValue)) && !/^\d+$/.test(statusValue);
  
  // 3. Overall success condition
  const isDone = TICK_ONLY_STRINGS.includes(statusValue) || isDate;

  const color = isDone ? '#4CAF50' : '#F44336';
  const indicator = isDone ? '✅' : '❌';

  // 4. Clean Display Logic: Date for docs, Empty for ticks, 'Outstanding' for ❌
  let displayValue = ''; 
  if (isDate) {
    displayValue = new Date(statusValue).toLocaleDateString('en-ZA'); 
  } else if (!isDone) {
    displayValue = 'Outstanding';
  }

  return (
    <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="space-between">
      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem' }}>
        {label}:
      </Typography>
      <Typography variant="caption" sx={{ fontWeight: 'bold', color: color, fontSize: '0.68rem' }}>
        {displayValue} {indicator}
      </Typography>
    </Stack>
  );
};

export default function MatterCard({ matter, onDelete, onUpdateStatus, isAdmin, statusTooltips, milestones = {}, isDeletedView, onRestore }) {
  const navigate = useNavigate();

  // 1. Destructure and rename to avoid 'status' initialization conflict
  const { 
    id, 
    matterName, 
    status: matterStatus, 
    documents = [], 
    client,
    estateNo,
    afuName,
    afuSurname,
    isSetAside,
    isNotProceeding,
    estateClassification,
  } = matter;

  // NPA / Curator Appointments don't use Prov Court Orders, Appointment Certs,
  // Final Court Orders or 1st/2nd Meetings – the Letter of Curatorship is final.
  const isNPA = estateClassification === 'NPA';

  // NPA matters: the AFU name on the card is the NPA's contact person, not the trustee, and
  // the appointee on an NPA matter is a Curator (stored in the same trustee1..8 fields as
  // trustees/liquidators on other matter types).
  const isNPAMatter = estateClassification === 'NPA' || estateClassification === 'Npa Matters' || estateClassification === 'Curator Appointments';
  const curatorNames = [1, 2, 3, 4, 5, 6, 7, 8]
    .map((i) => matter[`trustee${i}FullName`])
    .filter(Boolean)
    .join(', ');

  // 2. State hooks - Initialized with renamed matterStatus
  const [openModal, setOpenModal] = useState(false);
  const [newStatus, setNewStatus] = useState(matterStatus || 'pending');
  const [loading, setLoading] = useState(false);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [guestEmail, setGuestEmail] = useState('');
  const [inviteLoading, setInviteLoading] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  // 3. Milestone state management
  const [fetchedMilestones, setFetchedMilestones] = useState(() => {
    if (milestones && Object.keys(milestones).length > 0) return milestones;
    
    return {
      provisionalCertificate: matter.provisionalCertificate || '',
      provincalCourt: matter.provincalCourt || matter.provincialCourt || matter.courtOrder || '',
      finalCertificate: matter.finalCertificate || '',
      firstMeeting: matter.firstMeeting || '',
      secondMeeting: matter.secondMeeting || '',
      ldAccount: matter.ldAccount || '',
      confirmation: matter.confirmation || '',
    };
  });

  const {
    provisionalCertificate,
    provincalCourt,
    finalCertificate,
    firstMeeting,
    secondMeeting,
    ldAccount,
    confirmation
  } = fetchedMilestones;

  const statusColors = {
    'in progress': 'green',
    pending: 'orange',
    closed: 'red',
  };

  const handleClick = () => {
    if (matter.estateClassification === 'Deceased Estates') {
      navigate(`/deceased-estate/${id}`);
    } else {
      navigate(`/matter/${id}`);
    }
  };

  const handleDeleteClick = (event) => {
    event.stopPropagation();
    onDelete(id);
  };

  const handleRestoreClick = (event) => {
    event.stopPropagation();
    if (onRestore) onRestore(id);
  };

  const handleStatusChange = (event) => {
    setNewStatus(event.target.value);
  };

  const handleStatusUpdate = async () => {
    try {
      setLoading(true);
      await onUpdateStatus(id, newStatus);
      setOpenModal(false);
      setStatusMessage('Status updated successfully!');
      setSnackbarOpen(true);
    } catch (error) {
      console.error('Error updating status:', error);
      setStatusMessage('Failed to update status.');
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchMilestones = async () => {
      try {
        const response = await fetch(`/api/milestones/fetch/${id}`);
        if (!response.ok) throw new Error('Failed to fetch milestones');
        const data = await response.json();
        setFetchedMilestones(data);
      } catch (error) {
        console.error(`Error fetching milestones for ${id}:`, error);
      }
    };
    if (id) fetchMilestones();
  }, [id]);

  const handleInviteGuest = async () => {
    try {
      setInviteLoading(true);
      const response = await fetch(`/api/matter/${id}/invite`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: guestEmail }),
      });
      if (response.ok) {
        setStatusMessage('Guest invited successfully.');
      } else {
        const data = await response.json();
        setStatusMessage(`Invite failed: ${data.message || 'Unknown error'}`);
      }
      setSnackbarOpen(true);
      setInviteDialogOpen(false);
      setGuestEmail('');
    } catch (error) {
      console.error('Error inviting guest:', error);
    } finally {
      setInviteLoading(false);
    }
  };

  const tooltipDescription = statusTooltips[String(matterStatus).toLowerCase()] || "No description available.";

  return (
    <>
      <Card sx={{ height: 480, display: 'flex', flexDirection: 'column', position: 'relative' }}>
        {isAdmin && (
          <Box sx={{ position: 'absolute', top: 8, right: 8, zIndex: 10 }}>
            {isDeletedView ? (
              <Stack direction="row" spacing={0.5}>
                <IconButton onClick={handleRestoreClick} sx={{ color: 'success.main', bgcolor: 'white' }} size="small">
                  <RestoreFromTrashIcon fontSize="small" />
                </IconButton>
                <IconButton onClick={handleDeleteClick} sx={{ color: 'error.main', bgcolor: 'white' }} size="small">
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Stack>
            ) : (
              <IconButton onClick={handleDeleteClick} sx={{ color: 'error.main', bgcolor: 'white' }} size="small">
                <DeleteIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
        )}

        <Stack spacing={1.5} sx={{ p: 2.5, flex: 1, overflow: 'hidden' }}>
          <Link href="#" color="inherit" underline="hover" onClick={handleClick}>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }} noWrap>
              {matterName}
            </Typography>
          </Link>

          {(isSetAside || isNotProceeding) && (
            <Chip
              size="small"
              color="error"
              variant="outlined"
              label={isSetAside && isNotProceeding ? 'Set Aside / Not Proceeding' : isSetAside ? 'Set Aside' : 'Not Proceeding'}
              sx={{ fontWeight: 'bold', alignSelf: 'flex-start' }}
            />
          )}

          <Box sx={{ bgcolor: 'action.hover', p: 1, borderRadius: 1, borderLeft: '3px solid #1976d2' }}>
            <Typography variant="caption" display="block"><strong>Ref:</strong> {estateNo || 'N/A'}</Typography>
            <Typography variant="caption" display="block"><strong>Admin:</strong> {client || 'Not Assigned'}</Typography>
            {isNPAMatter ? (
              <>
                <Typography variant="caption" display="block"><strong>Curator:</strong> {curatorNames || 'N/A'}</Typography>
                <Typography variant="caption" display="block"><strong>NPA Contact:</strong> {afuName ? `${afuName} ${afuSurname || ''}`.trim() : 'N/A'}</Typography>
              </>
            ) : (
              <Typography variant="caption" display="block"><strong>Trustee:</strong> {afuName ? `${afuName} ${afuSurname}` : 'N/A'}</Typography>
            )}
          </Box>

          <Divider />

          {/* 🎯 MILESTONES SECTION */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.4 }}>
            {isNPA ? (
              <>
                <MilestoneStatus label="Letter of Curatorship" status={provisionalCertificate} />
                <MilestoneStatus label="L&D Account" status={ldAccount} />
                <MilestoneStatus label="Confirmation" status={confirmation} />
              </>
            ) : (
              <>
                <MilestoneStatus label="Prov. Court Order" status={provincalCourt} />
                <MilestoneStatus label="Prov. Appointment Cert." status={provisionalCertificate} />
                <MilestoneStatus label="Final Order Cert." status={finalCertificate} />
                <MilestoneStatus label="1st Meeting" status={firstMeeting} />
                <MilestoneStatus label="2nd Meeting" status={secondMeeting} />
                <MilestoneStatus label="L&D Account" status={ldAccount} />
                <MilestoneStatus label="Confirmation" status={confirmation} />
              </>
            )}
          </Box>

          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mt: 'auto', pt: 1 }}>
            <Tooltip title={tooltipDescription} arrow>
              <Box sx={{
                  backgroundColor: statusColors[String(matterStatus).toLowerCase()] || 'gray',
                  color: 'white', px: 1, py: 0.5, borderRadius: 1, fontSize: '10px', fontWeight: 'bold', textTransform: 'uppercase'
                }}
              >
                {matterStatus}
              </Box>
            </Tooltip>

            {isAdmin && !isDeletedView && (
              <Stack direction="row" spacing={1}>
                <Button variant="outlined" size="small" sx={{ fontSize: '0.65rem' }} onClick={() => setOpenModal(true)}>Update</Button>
                <Button variant="outlined" size="small" sx={{ fontSize: '0.65rem' }} onClick={() => setInviteDialogOpen(true)}>Invite</Button>
              </Stack>
            )}
          </Stack>
        </Stack>
      </Card>

      {/* Modals & Snackbar logic remain same */}
      <Modal open={openModal} onClose={() => setOpenModal(false)} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Fade in={openModal}>
          <Box sx={{ bgcolor: 'background.paper', p: 4, borderRadius: 2, width: 300 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>Update Status</Typography>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select value={newStatus} label="Status" onChange={handleStatusChange}>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="in progress">In Progress</MenuItem>
                <MenuItem value="closed">Closed</MenuItem>
              </Select>
            </FormControl>
            <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
              <Button fullWidth onClick={() => setOpenModal(false)} variant="outlined">Cancel</Button>
              <Button fullWidth onClick={handleStatusUpdate} color="primary" variant="contained" disabled={loading}>
                {loading ? <CircularProgress size={24} /> : 'Update'}
              </Button>
            </Stack>
          </Box>
        </Fade>
      </Modal>

      <Modal open={inviteDialogOpen} onClose={() => setInviteDialogOpen(false)} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Fade in={inviteDialogOpen}>
          <Box sx={{ bgcolor: 'background.paper', p: 4, borderRadius: 2, width: 300 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>Invite Guest</Typography>
            <TextField fullWidth label="Guest Email" size="small" value={guestEmail} onChange={(e) => setGuestEmail(e.target.value)} sx={{ mb: 2 }} />
            <Stack direction="row" spacing={2}>
              <Button onClick={() => setInviteDialogOpen(false)} variant="outlined">Cancel</Button>
              <Button onClick={handleInviteGuest} color="primary" variant="contained" disabled={inviteLoading}>Send</Button>
            </Stack>
          </Box>
        </Fade>
      </Modal>

      <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={() => setSnackbarOpen(false)}>
        <Alert severity={statusMessage.includes('failed') ? 'error' : 'success'}>{statusMessage}</Alert>
      </Snackbar>
    </>
  );
}

MatterCard.propTypes = {
  matter: PropTypes.object.isRequired,
  onDelete: PropTypes.func.isRequired,
  onUpdateStatus: PropTypes.func.isRequired,
  isAdmin: PropTypes.bool.isRequired,
  statusTooltips: PropTypes.object.isRequired,
  isDeletedView: PropTypes.bool,
  onRestore: PropTypes.func,
  milestones: PropTypes.object
};